// services/planService.js

import { meService } from 'src/api/authentication'
import { createStripeSessionService } from 'src/api/payment'
import { customerUpgradePortal } from 'src/api/limit'
import { loadStripe } from '@stripe/stripe-js'
import { track } from '@amplitude/analytics-browser'
import { ROLE_TYPES } from '../data/pricing-json'
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY)

// "Session for Mini Course Generator's Custom Plan" — the general demo link.
export const BOOK_A_CALL_URL = 'https://calendar.app.google/wtkYotVHXpbzyKp19'
// Each enterprise plan books its own slot: "Meet with Mini Course Generator
// (Growth)" and "Meet with MCG Team" for the headless plan.
export const BOOK_A_CALL_GROWTH_URL =
  'https://calendar.app.google/6Pnm79pajfhp8Eny9'
export const BOOK_A_CALL_HEADLESS_URL =
  'https://calendar.app.google/afP5X5MKDyRcVZf8A'

export const handleStripeCheckout = async (
  priceId,
  priceTrialDays,
  successReturnUrl,
) => {
  await meService()
  const toltReferral = window.tolt_referral || null
  const resStripeSession = await createStripeSessionService(
    priceId,
    priceTrialDays,
    successReturnUrl,
    toltReferral,
  )

  const stripe = await stripePromise
  await stripe.redirectToCheckout({
    sessionId: resStripeSession.data.sessionId,
  })
}

export const handlePortalRedirection = async () => {
  const response = await customerUpgradePortal()
  window.open(response.data.sessionUrl)
}

export const trackPlanUpgrade = subscription => {
  track('upgrade_clicked', { subscription })
}

/**
 * How a user on `groupId` starts a plan change:
 *  - 'checkout' → no active subscription, send them to Stripe checkout
 *  - 'portal'   → already subscribed, send them to the Stripe customer portal
 *  - 'none'     → plan changes aren't self-serve (AppSumo lifetime deals)
 */
export const getCheckoutMode = groupId => {
  if (groupId === ROLE_TYPES.BASIC || groupId === ROLE_TYPES.STANDARD) {
    return 'checkout'
  }
  if (groupId === ROLE_TYPES.APPSUMO || groupId == null) return 'none'
  return 'portal'
}

// Display name per card key.
const CARD_LABEL = {
  plus: 'Plus',
  pro: 'Pro',
}

// Enterprise cards lead with a demo call; the upgrade button beside it checks
// out the plan's underlying subscription (Growth / Scale).
const DEMO_CARDS = ['marketing', 'inapp']

// Position of each purchasable card on the plan ladder.
const CARD_RANK = { plus: 1, pro: 2 }

// The card a user is currently ON, if that plan is still sold.
const GROUP_TO_CARD = {
  [ROLE_TYPES.PLUS]: 'plus',
  [ROLE_TYPES.PRO_V2]: 'pro',
}

// Ladder position of a user's current plan, including grandfathered plans that
// no longer have a card. Anything at or below this rank is a downgrade
// ("Switch to X") rather than an upgrade.
const GROUP_RANK = {
  [ROLE_TYPES.APPSUMO]: 0,
  [ROLE_TYPES.BASIC]: 0,
  [ROLE_TYPES.STANDARD]: 0,
  [ROLE_TYPES.PREMIUM]: 1,
  [ROLE_TYPES.PRO]: 1, // legacy Pro (role 24), not the new Pro v2
  [ROLE_TYPES.PLUS]: 1,
  [ROLE_TYPES.PRO_V2]: 2,
  [ROLE_TYPES.ESSENTIALS]: 2, // grandfathered; same price/learners as Pro
  // Grandfathered enterprise plans (no card any more) sit above both creator
  // plans, so the creator cards read "Switch to".
  [ROLE_TYPES.ELITE]: 3,
  [ROLE_TYPES.LAUNCH]: 3,
  [ROLE_TYPES.GROWTH]: 4,
  [ROLE_TYPES.SCALE]: 5,
}

// Which role a user must already be on for an enterprise card to be their plan,
// and where each card sits on that same ladder. Role 31 ("Enterprise") groups
// as Scale, so those users see the Growth card as a downgrade too.
const ENTERPRISE_CARD_GROUP = {
  marketing: ROLE_TYPES.GROWTH,
  inapp: ROLE_TYPES.SCALE,
}
const ENTERPRISE_CARD_RANK = { marketing: 4, inapp: 5 }
const ENTERPRISE_CARD_LABEL = { marketing: 'Growth', inapp: 'Customer Success' }

/** Secondary "Upgrade" action on the enterprise cards, beside Book A Demo. */
export const getUpgradeAction = (groupId, cardKey) => {
  if (ENTERPRISE_CARD_GROUP[cardKey] === groupId) {
    return { text: 'Current Plan', disabled: true }
  }

  // Scale / Enterprise subscribers looking at the Growth card are moving down,
  // so it reads "Switch", like the creator cards do.
  if ((GROUP_RANK[groupId] ?? 0) > ENTERPRISE_CARD_RANK[cardKey]) {
    return {
      text: `Switch to ${ENTERPRISE_CARD_LABEL[cardKey]}`,
      tableText: 'Switch',
      disabled: false,
      active: true,
    }
  }

  return {
    text: 'Upgrade',
    // AppSumo lifetime users can't self-serve a plan change.
    disabled: getCheckoutMode(groupId) === 'none',
  }
}

export const getAction = (groupId, cardKey) => {
  if (DEMO_CARDS.includes(cardKey)) {
    return {
      text: 'Book A Demo',
      tableText: 'Book A Demo',
      disabled: false,
      isCustom: true,
    }
  }

  const label = CARD_LABEL[cardKey]

  if (GROUP_TO_CARD[groupId] === cardKey) {
    return {
      text: 'Current Plan',
      tableText: 'Current Plan',
      disabled: true,
      background: '#F4F6F8',
    }
  }

  const currentRank = GROUP_RANK[groupId] ?? 0

  if (CARD_RANK[cardKey] <= currentRank) {
    return {
      text: `Switch to ${label}`,
      tableText: 'Switch',
      disabled: false,
      active: true,
      background: '#F4F6F8',
    }
  }

  return {
    text: `Upgrade to ${label}`,
    tableText: 'Upgrade',
    // AppSumo lifetime users can't self-serve a plan change.
    disabled: getCheckoutMode(groupId) === 'none',
    background: '#E3F3FF',
  }
}
